import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { v4 as uuidv4 } from 'uuid';
import { getAddinOutlookEmailAssistantDeployment, getAddinOutlookCustomReadViewDeployment, } from '../services/agent';
import { useOfficeTheme } from '../lib/useOfficeTheme';
import { useOidcMessageHandler } from '../hooks/useOidcMessageHandler';
import AuthNavigator from './auth/AuthNavigator';
import { McpSessionProvider } from './McpSessionProvider';
import { ChatProvider } from './chat/ChatContext';
import OutlookChat from './chat/OutlookChat';
import { MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { LoadingSpinner } from '../ui/loading-spinner';
import { isComposeMode as checkIsComposeMode } from '../types/office';
const OutlookApp = () => {
    const { isAuthenticated, user } = useAuth();
    const auth = useAuth();
    const chatId = useMemo(() => uuidv4(), []);
    const [agentId, setAgentId] = useState();
    // undefined = not yet checked, null = no custom deployment, string = has custom deployment
    const [customReadViewAgentId, setCustomReadViewAgentId] = useState(undefined);
    const theme = useOfficeTheme();
    const [showClearButton, setShowClearButton] = useState(false);
    const [isComposeMode, setIsComposeMode] = useState(false);
    // Handle OIDC messages from auth popup
    useOidcMessageHandler();
    const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
    useEffect(() => {
        // Use ref to track key sequence without causing re-renders
        const keySequence = [];
        const handleKeyDown = (e) => {
            // Only track arrow keys for Konami code (ignore other keys like typing)
            if (!e.key.startsWith('Arrow')) {
                return;
            }
            keySequence.push(e.key);
            if (keySequence.length > 6) {
                keySequence.shift(); // Keep only last 6 keys
            }
            // Check if the sequence matches the Konami code
            if (keySequence.length === 6 &&
                keySequence.every((key, index) => key === KONAMI_CODE[index])) {
                setShowClearButton((current) => !current); // Toggle visibility
                keySequence.length = 0; // Reset sequence
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    useEffect(() => {
        if (user && isAuthenticated && user.access_token) {
            const fetchDeployments = async () => {
                // Fetch main email assistant deployment (required)
                const deployment = await getAddinOutlookEmailAssistantDeployment(user.access_token);
                setAgentId(deployment.pipelineId);
                // Fetch optional custom read view deployment
                const customDeployment = await getAddinOutlookCustomReadViewDeployment(user.access_token);
                // Explicitly set to pipelineId or null to signal loading is complete
                setCustomReadViewAgentId(customDeployment?.pipelineId ?? null);
            };
            fetchDeployments();
        }
    }, [user, isAuthenticated]);
    // Detect compose mode with retry mechanism
    useEffect(() => {
        let retryCount = 0;
        const maxRetries = 5;
        const retryDelay = 200; // ms
        const detectMode = () => {
            if (Office.context.mailbox?.item) {
                // Use type guard for compose mode detection
                const compose = checkIsComposeMode(Office.context.mailbox.item);
                setIsComposeMode(compose);
            }
            else if (retryCount < maxRetries) {
                // Retry if item isn't ready yet
                retryCount++;
                setTimeout(detectMode, retryDelay);
            }
        };
        detectMode();
    }, []);
    const handleClearSession = async () => {
        // Clear OIDC auth
        await auth.removeUser();
        // Clear all stored data
        localStorage.clear();
        sessionStorage.clear();
        // Reload to restart the app
        window.location.reload();
    };
    const OutlookMainPage = () => {
        // Wait for both main deployment and custom read view check to complete
        // (customReadViewAgentId is undefined while loading, null or string when done)
        if (!agentId || customReadViewAgentId === undefined) {
            return (_jsx("div", { className: "h-screen bg-background flex items-center justify-center", children: _jsx(LoadingSpinner, {}) }));
        }
        return (_jsx("div", { className: `h-screen ${theme.isDark ? 'dark' : ''}`, children: _jsx("div", { className: "h-full bg-background text-foreground", children: _jsx(ChatProvider, { conversationId: chatId, agentId: agentId, children: _jsx(OutlookChat, { isComposeMode: isComposeMode, customReadViewAgentId: customReadViewAgentId }) }) }) }));
    };
    const ClearSessionButton = () => {
        if (!showClearButton)
            return null;
        return (_jsx("div", { className: "absolute top-2 right-2 z-50", children: _jsxs(DropdownMenu, { children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx(Button, { variant: "ghost", size: "icon", className: "h-8 w-8", children: _jsx(MoreVertical, { className: "h-4 w-4" }) }) }), _jsx(DropdownMenuContent, { className: "bg-opacity-100 bg-white", align: "end", children: _jsx(DropdownMenuItem, { className: "focus:cursor-pointer focus:outline-gray-400", onClick: handleClearSession, children: "Clear Session" }) })] }) }));
    };
    return (_jsxs("div", { className: `h-screen ${theme.isDark ? 'dark' : ''} relative`, children: [_jsx(ClearSessionButton, {}), isAuthenticated ? (_jsx(McpSessionProvider, { children: _jsx(OutlookMainPage, {}) })) : (_jsx(AuthNavigator, {}))] }));
};
export default OutlookApp;
